import {PayloadAction} from "@reduxjs/toolkit";
import {OrderBook, SecurityExpression, SecuritySnapshot} from "../proto/generated/security";
import {getSecurityKey} from "../util/security";
import { CandleChange } from "../components/candleChangeManager/candleChangeManager";
import { ActionType } from "./common.reducer";
import securityActions from "../actionTypes/security.actionTypes";
import { OptionGreeks } from "../proto/generated/common";

export type Snapshot = Omit<SecuritySnapshot, "security">

export interface SecurityStateType {
    mainSecurity: string,
    overviewSecurity: string,
    securitiesLoaded: boolean,
    snapshots: Record<string, Snapshot>,
    orderBooks: Record<string, OrderBook>,
    candleChangeBook: Record<string, CandleChange>,
    greeks: Record<string, OptionGreeks>,
    expression: {
        enabled: boolean,
        value: string,
        expressionSymbols: string[]
    },
    referenceSecurityMap: Record<string, string>,
    userExpressions: SecurityExpression[] | undefined,
    lastRefreshTime: Number
}

export const securityInitialState: SecurityStateType = {
    mainSecurity: "",
    overviewSecurity: "",
    securitiesLoaded: false,
    snapshots: {},
    orderBooks: {},
    candleChangeBook: {},
    greeks: {},
    expression: {
        enabled: false,
        value: "",
        expressionSymbols: []
    },
    referenceSecurityMap: {},
    userExpressions: undefined,
    lastRefreshTime: 0
};

const getUpdatedLiveData = (state: SecurityStateType, action: PayloadAction<{snapshots?: Partial<SecuritySnapshot>[], orderBooks?: OrderBook[], candleChangeBooks?: CandleChange[], greeks?: {securityKey: string, greeks: OptionGreeks}[]}>) => {
    const {snapshots, orderBooks, candleChangeBooks, greeks} = action.payload
    let nextSnapshots = state.snapshots
    let nextReferenceMap = state.referenceSecurityMap
    let nextOrderBooks = state.orderBooks
    let nextCandleChangeBook = state.candleChangeBook
    let nextGreeks = state.greeks
    let snapshotsDraft: Record<string, Snapshot> | null = null
    let referenceDraft: Record<string, string> | null = null
    let orderBooksDraft: Record<string, OrderBook> | null = null
    let candleDraft: Record<string, CandleChange> | null = null
    let greeksDraft: Record<string, OptionGreeks> | null = null

    if (snapshots) {
        for (const snap of snapshots) {
            if (!snap?.security) continue
            const {security, lastTradedPrice, volume, previousClose, lastTradedTime, referencedSecurity} = snap
            const key = getSecurityKey(security.exchange, security.code)
            if (referencedSecurity && !state.referenceSecurityMap[key] && !(referenceDraft && referenceDraft[key])) {
                if (!referenceDraft) referenceDraft = { ...state.referenceSecurityMap }
                referenceDraft[key] = getSecurityKey(referencedSecurity.exchange, referencedSecurity.code)
            }
            const prevSnapshot = (snapshotsDraft ?? state.snapshots)[key]
            const nextSnapshot: Snapshot = {
                lastTradedPrice: lastTradedPrice || prevSnapshot?.lastTradedPrice || 0,
                volume: volume || prevSnapshot?.volume || 0,
                previousClose: previousClose || prevSnapshot?.previousClose || 0,
                lastTradedTime: lastTradedTime || prevSnapshot?.lastTradedTime || 0,
                referencedSecurity
            }
            if (
                prevSnapshot
                && prevSnapshot.lastTradedPrice === nextSnapshot.lastTradedPrice
                && prevSnapshot.volume === nextSnapshot.volume
                && prevSnapshot.previousClose === nextSnapshot.previousClose
                && prevSnapshot.lastTradedTime === nextSnapshot.lastTradedTime
                && prevSnapshot.referencedSecurity === nextSnapshot.referencedSecurity
            ) {
                continue
            }
            if (!snapshotsDraft) snapshotsDraft = { ...state.snapshots }
            snapshotsDraft[key] = nextSnapshot
        }
        if (snapshotsDraft) nextSnapshots = snapshotsDraft
        if (referenceDraft) nextReferenceMap = referenceDraft
    }

    if (orderBooks) {
        for (const orderBook of orderBooks) {
            const {security} = orderBook
            if (!security) continue
            const key = getSecurityKey(security.exchange, security.code)
            if ((orderBooksDraft ?? state.orderBooks)[key] === orderBook) continue
            if (!orderBooksDraft) orderBooksDraft = { ...state.orderBooks }
            orderBooksDraft[key] = orderBook
        }
        if (orderBooksDraft) nextOrderBooks = orderBooksDraft
    }

    if (candleChangeBooks) {
        for (const book of candleChangeBooks) {
            const {security} = book
            if (!security) continue
            const key = getSecurityKey(security.exchange, security.code)
            if ((candleDraft ?? state.candleChangeBook)[key] === book) continue
            if (!candleDraft) candleDraft = { ...state.candleChangeBook }
            candleDraft[key] = book
        }
        if (candleDraft) nextCandleChangeBook = candleDraft
    }

    if (greeks) {
        for (const {securityKey, greeks: optionGreeks} of greeks) {
            if (!securityKey || !optionGreeks) continue
            if ((greeksDraft ?? state.greeks)[securityKey] === optionGreeks) continue
            if (!greeksDraft) greeksDraft = { ...state.greeks }
            greeksDraft[securityKey] = optionGreeks
        }
        if (greeksDraft) nextGreeks = greeksDraft
    }

    if (
        nextSnapshots === state.snapshots
        && nextReferenceMap === state.referenceSecurityMap
        && nextOrderBooks === state.orderBooks
        && nextCandleChangeBook === state.candleChangeBook
        && nextGreeks === state.greeks
    ) {
        return state
    }

    return {
        ...state,
        snapshots: nextSnapshots,
        referenceSecurityMap: nextReferenceMap,
        orderBooks: nextOrderBooks,
        candleChangeBook: nextCandleChangeBook,
        greeks: nextGreeks
    }
}

export default function securityReducer(state = securityInitialState, action: ActionType) {
    const { type, payload } = action;
    let newState: SecurityStateType = state;
    switch (type) {
        case securityActions.UPDATE_MAIN_SECURITY:
            return {
                ...state,
                mainSecurity: payload
            }
        case securityActions.UPDATE_OVERVIEW_SECURITY:
            return {
                ...state,
                overviewSecurity: payload
            }
        case securityActions.SET_SECURITIES_LOADED:
            return {
                ...state,
                securitiesLoaded: payload
            }
        case securityActions.UPDATE_SNAPSHOTS:
            return getUpdatedLiveData(state, {type: action.type, payload: {snapshots: action.payload}});
        case securityActions.UPDATE_ORDER_BOOKS:
            return getUpdatedLiveData(state, {type: action.type, payload: {orderBooks: action.payload}});
        case securityActions.UPDATE_CANDLE_CHANGE_BOOKS:
            return getUpdatedLiveData(state, {type: action.type, payload: {candleChangeBooks: action.payload}});
        case securityActions.REMOVE_CANDLE_CHANGE_BOOK_ENTRIES: {
            const keys: string[] = action.payload ?? []
            if (!keys.length) return state
            const candleChangeBook = { ...state.candleChangeBook }
            keys.forEach((key) => delete candleChangeBook[key])
            return { ...state, candleChangeBook }
        }
        case securityActions.UPDATE_GREEKS:
            return getUpdatedLiveData(state, {type: action.type, payload: {greeks: action.payload}});
        case securityActions.UPDATE_LIVE_DATA:
            return getUpdatedLiveData(state, action);
        case securityActions.SAVE_EXPRESSION:
            newState = {...state, expression: {...state.expression}};

            if (action.payload && action.payload.expression && action.payload.expression.length > 0) {
                newState.expression.value = action.payload.expression
                newState.expression.enabled = true
                newState.expression.expressionSymbols = action.payload.expressionSymbols;
            } else {
                newState.expression.value = "";
                newState.expression.enabled = false;
                newState.expression.expressionSymbols = [];
            }
            return newState;
        case securityActions.SAVE_USER_EXPRESSIONS:
            return {
                ...state,
                userExpressions: action.payload
            }
        case securityActions.ADD_OR_UPDATE_USER_EXPRESSION:
            newState = {...state};
            const newUserExpressions = (state.userExpressions ?? []).filter((expression) => (expression.id !== action.payload.id))
            newUserExpressions.push(action.payload)
            newState.userExpressions = newUserExpressions
            return newState;
        case securityActions.DELETE_USER_EXPRESSIONS:
            return {
                ...state,
                userExpressions: state.userExpressions?.filter((expression) => (expression.id !== action.payload.id))
            };
        default:
            return state
    }
}

const updateMainSecurity = (mainSecurity: string) => ({
    type: securityActions.UPDATE_MAIN_SECURITY,
    payload: mainSecurity
})

const setSecuritiesLoaded = (securitiesLoaded: boolean) => ({
    type: securityActions.SET_SECURITIES_LOADED,
    payload: securitiesLoaded
});

const updateOverviewSecurity = (overviewSecurity: string) => ({
    type: securityActions.UPDATE_OVERVIEW_SECURITY,
    payload: overviewSecurity
});

const updateSnapshots = (snapshots: Partial<SecuritySnapshot>[]) => ({
    type: securityActions.UPDATE_SNAPSHOTS,
    payload: snapshots
});

const updateOrderBooks = (orderBooks: OrderBook[]) => ({
    type: securityActions.UPDATE_ORDER_BOOKS,
    payload: orderBooks
})

const updateCandleChangeBook = (candleChangeBooks: CandleChange[]) => ({
    type: securityActions.UPDATE_CANDLE_CHANGE_BOOKS,
    payload: candleChangeBooks
});

const removeCandleChangeBookEntries = (securityKeys: string[]) => ({
    type: securityActions.REMOVE_CANDLE_CHANGE_BOOK_ENTRIES,
    payload: securityKeys
});

const updateGreeks = (greeks: {securityKey: string, greeks: OptionGreeks}[]) => ({
    type: securityActions.UPDATE_GREEKS,
    payload: greeks
});

const updateLiveData = ({snapshots, orderBooks, greeks}: {snapshots?: Partial<SecuritySnapshot>[], orderBooks?: OrderBook[], greeks?: {securityKey: string, greeks: OptionGreeks}[]}) => ({
    type: securityActions.UPDATE_LIVE_DATA,
    payload: {snapshots, orderBooks, greeks}
});

const saveExpression = (expression: any) => ({
    type: securityActions.SAVE_EXPRESSION,
    payload: expression
});

const saveUserExpressions = (expressions: SecurityExpression[]) => ({
    type: securityActions.SAVE_USER_EXPRESSIONS,
    payload: expressions
})

const addOrUpdateUserExpression = (expression: SecurityExpression) => ({
    type: securityActions.ADD_OR_UPDATE_USER_EXPRESSION,
    payload: expression
});

const deleteUserExpressions = (expression: SecurityExpression) => ({
    type: securityActions.DELETE_USER_EXPRESSIONS,
    payload: expression
})


export { updateMainSecurity, updateOverviewSecurity, setSecuritiesLoaded, updateSnapshots, updateOrderBooks, updateCandleChangeBook,
    removeCandleChangeBookEntries, updateGreeks, updateLiveData, saveExpression, saveUserExpressions, addOrUpdateUserExpression, deleteUserExpressions}
